// mcn-frontend/src/components/layout/Header.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import NotificationBell from './NotificationBell';
import './Header.css';

/**
 * Hardcoded fallback nav for unauthenticated visitors — used when headerData
 * has not loaded yet, so we never blank out the header.
 */
const FALLBACK_PUBLIC_NAV = [
  { label: 'Projects', href: '/projects' },
  { label: 'List Your Project', href: '/list-your-project' },
  { label: 'Channels', href: '/channels' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Apply', href: '/apply' },
];

const Header = ({ headerData }) => {
  const { 
    user, 
    currentUser, 
    isAuthenticated, 
    logout, 
    selectedChannel, 
    userChannels,
    selectChannel,
    canUpload 
  } = useUser();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);
  const navigate = useNavigate();

  // Use currentUser if user is not available (for backward compatibility)
  const activeUser = user || currentUser;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUploadClick = () => {
    if (!selectedChannel) {
      alert('Please select a channel first');
      navigate('/channel-dashboard');
    } else {
      navigate('/upload');
    }
  };

  const handleChannelSelect = (channel) => {
    selectChannel(channel);
    setShowChannelDropdown(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo">
          <span className="logo-text">
            {headerData?.logo?.primaryText || 'Choice'}
            {headerData?.logo?.accentText ? ` ${headerData.logo.accentText}` : ' MCN'}
          </span>
        </Link>

        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
          {isAuthenticated ? (
            <>
              {/* Show Upload button for channel owners and creators */}
              {canUpload && (
                <>
                  <button className="upload-btn" onClick={handleUploadClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v20M2 12h20"/>
                    </svg>
                    Upload Video
                  </button>
                  
                  {/* Channel Selector Dropdown */}
                  {userChannels && userChannels.length > 0 && (
                    <div className="channel-selector">
                      <button 
                        className="channel-selector-btn"
                        onClick={() => setShowChannelDropdown(!showChannelDropdown)}
                      >
                        <span className="channel-name">
                          {selectedChannel ? selectedChannel.name : 'Select Channel'}
                        </span>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                          <path d="M6 8L2 4h8z"/>
                        </svg>
                      </button>
                      
                      {showChannelDropdown && (
                        <div className="channel-dropdown">
                          {userChannels.map(channel => (
                            <button
                              key={channel.id}
                              className={`channel-option ${selectedChannel?.id === channel.id ? 'active' : ''}`}
                              onClick={() => handleChannelSelect(channel)}
                            >
                              <span className="channel-option-name">{channel.name}</span>
                              {channel.platform && (
                                <span className="channel-platform">{channel.platform}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Dashboard Link */}
              <Link
                to="/channel-dashboard"
                className="nav-link"
              >
                Dashboard
              </Link>

              {/* Reports Link */}
              <Link to="/reports" className="nav-link">
                Reports
              </Link>

              {/* Producer nav — any authenticated user can have listings.
                  We show "My Listings" for everyone except pure investors;
                  the page itself shows "No listings yet" with a CTA to /list-your-project
                  if the user has none, so it's safe to surface broadly. */}
              {activeUser?.role !== 'INVESTOR' && (
                <Link to="/my-listings" className="nav-link" title="Manage your project listings">
                  My Listings
                </Link>
              )}

              {/* Admin Links - only visible to admin roles.
                  Adds discoverable entry points to the admin sub-pages:
                    /admin/acquisitions      - movie acquisition pipeline (create/list movies)
                    /admin/listing-payments  - mark listing fees paid / waive
                    /admin/content           - homepage / header / footer CMS editor
                    /admin/invites           - bulk creator invite tool */}
              {['SUPER_ADMIN', 'ADMIN', 'TENANT_ADMIN'].includes(activeUser?.role) && (
                <>
                  <Link to="/admin/acquisitions" className="nav-link" title="Acquisition Pipeline - add movies, advance pipeline">
                    Acquisitions
                  </Link>
                  <Link to="/admin/listing-payments" className="nav-link" title="Mark listing fees paid or waive">
                    Payments
                  </Link>
                  <Link to="/admin/listing-review" className="nav-link" title="Approve or reject paid listings">
                    Review
                  </Link>
                  <Link to="/admin/inquiries" className="nav-link" title="Inquiry inbox">
                    Inbox
                  </Link>
                  <Link to="/admin/content" className="nav-link" title="Edit homepage, header, footer">
                    CMS
                  </Link>
                  <Link to="/admin/invites" className="nav-link" title="Bulk creator invites">
                    Invites
                  </Link>
                </>
              )}

              {/* User Menu */}
              <div className="user-menu" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <NotificationBell />
                <Link to="/settings" className="nav-link" style={{ marginRight: 8 }}>
                  Settings
                </Link>
                <span className="user-name">
                  Hi, {
                    activeUser?.firstName ||
                    activeUser?.name ||
                    activeUser?.fullName ||
                    (activeUser?.email ? activeUser.email.split('@')[0] : 'there')
                  }
                </span>
                <button onClick={handleLogout} className="logout-btn">Logout</button>
              </div>
            </>
          ) : (
            <>
              {(headerData?.navigation && headerData.navigation.length > 0
                ? headerData.navigation
                : FALLBACK_PUBLIC_NAV
              ).map((item) => {
                const href = item.href || '#';
                // Anchor links (e.g. "#features") work as plain <a> on homepage
                // and gracefully no-op on inner pages.
                const isAnchor = href.startsWith('#');
                // External URLs open in new tab.
                const isExternal = /^https?:\/\//i.test(href);

                if (isExternal) {
                  return (
                    <a
                      key={item.label}
                      href={href}
                      className="nav-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.label}
                    </a>
                  );
                }
                if (isAnchor) {
                  return (
                    <a key={item.label} href={href} className="nav-link">
                      {item.label}
                    </a>
                  );
                }
                return (
                  <Link key={item.label} to={href} className="nav-link">
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to={headerData?.buttons?.loginLink || '/login'}
                className="header-login-btn"
              >
                {headerData?.buttons?.loginText || 'Login'}
              </Link>
              <Link to="/signup" className="header-signup-btn">
                {headerData?.buttons?.applyText || 'Sign Up'}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;